import { useCallback, useEffect } from "react";
import { useAuth } from "domain/auth/authContext";
import { AsyncStatus } from "lib/enums";

const intercomAppId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

const Intercom = () => {
  const { bootIntercom } = useIntercom();

  // Boot Intercom.
  useEffect(() => {
    bootIntercom();
  }, [bootIntercom]);

  return null;
};

export default Intercom;

export const useIntercom = () => {
  const {
    state: { hasValidatedSavedToken, dataUser, dataProfile, status },
  } = useAuth();

  /**
   * Boots Intercom. Sends user properties if possible.
   */
  const bootIntercom = useCallback(() => {
    if (!window.Intercom) {
      // Wait for `window.Intercom` to become available.
      setTimeout(bootIntercom, 1000);
    } else {
      // Wait for token validation to be completed.
      if (hasValidatedSavedToken) {
        // Wait for user data to be loaded.
        if (dataUser) {
          // Wait for user profile data to be loaded.
          if (
            status.dataProfile === AsyncStatus.Success ||
            status.dataProfile === AsyncStatus.Error
          ) {
            window.Intercom("shutdown");
            window.Intercom("boot", {
              app_id: intercomAppId,
              user_id: dataUser.id,
              verified_email: dataUser.i765ApplicationInfo?.verifiedEmail,
              email: dataUser.email,
              name: dataProfile
                ? dataProfile.firstName + " " + dataProfile.lastName
                : undefined,
              avatar: dataProfile?.profileImage
                ? { type: "avatar", image_url: dataProfile.profileImage }
                : undefined,
              // Support and ops filter conversations on these, and the
              // backend still returns `selfApplicationInfo` for everyone who
              // ever bought one, so they keep being reported. What is gone is
              // the web funnel that could newly set them: `bootIntercom` used
              // to take a `{ purchased: true }` override, passed by the
              // self-application payment modal at the moment of purchase.
              activated_self_application: !!dataUser.selfApplicationInfo,
              purchased_self_application:
                !!dataUser.selfApplicationInfo?.isPurchased,
              lawfully_ops: dataUser.selfApplicationInfo
                ? `https://ops${
                    process.env.NEXT_PUBLIC_DIST_ENV === "production"
                      ? ""
                      : "-test"
                  }.lawfully.com/users/${dataUser.id}`
                : null,
            });
          }
        } else {
          window.Intercom("boot", {
            app_id: intercomAppId,
          });
        }
      }
    }
  }, [dataProfile, dataUser, hasValidatedSavedToken, status.dataProfile]);

  return {
    bootIntercom,
  };
};
